import { useClickOutside, useMediaQuery } from '@mnd-frontend/hooks';
import {
  Flag,
  GREY_300,
  GREY_400,
  SPACING_1,
  SPACING_2,
  SPACING_3,
  Tooltip,
  breakpoints,
  isFlagCode,
  media,
  useTranslation,
} from '@mnd-frontend/ui';
import { useRef } from 'react';
import styled from 'styled-components';
import MndLogotype from '../assets/mynewsdesk_logotype_large.png';
import { type MainMenuItem, MenuState } from '../types';
import SubMenu from './SubNavList';

const imgSizes = {
  mobile: '32px',
  desktop: '56px',
};

const NavItem = styled.li`
  background-color: transparent;
  height: var(--navigation-item-height);
  display: grid;
  place-items: center;
  padding: ${SPACING_1} 0;
  ${media.greaterThan('navigationBreakpoint')} {
    margin-bottom: ${SPACING_3};
  }
  ${media.compactDesktopNavigation()} {
    margin-bottom: ${SPACING_1};
    padding: 0;
  }
`;

const ImgWrapper = styled.div`
  width: var(--navigation-small-button-size);
  height: var(--navigation-small-button-size);
  ${media.greaterThan('navigationBreakpoint')} {
    width: var(--navigation-large-button-size);
    height: var(--navigation-large-button-size);
  }
  border-radius: 50%;
  overflow: hidden;
  position: relative;

  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ChangePressroomButton = styled.button`
  background-color: transparent;
  position: relative;
  border: 0;
  padding: 0;
  display: grid;
  place-items: center;

  ${ImgWrapper} {
    border: 1px solid ${GREY_400};
    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      background-color: transparent;
    }
  }
  &:focus-visible,
  &:hover {
    outline: 0;
    ${ImgWrapper}::after {
      background-color: rgba(0, 0, 0, 0.35);
    }
  }
`;
const FlagWrapper = styled.div<{ $isMobile?: boolean }>`
  position: absolute;
  z-index: 1;
  border: 2px solid ${GREY_300};
  background: ${GREY_300};
  border-radius: 50%;
  line-height: 0;
  bottom: ${props => (props.$isMobile ? '-0.25rem' : 0)};
  right: ${props => (props.$isMobile ? '-0.25rem' : 0)};
`;

const LogoSkeleton = styled.li`
  padding: ${SPACING_1} 0;
  ${media.greaterThan('navigationBreakpoint')} {
    height: var(--navigation-item-height);
  }
  margin: 0 auto;
  margin-bottom: ${SPACING_2};
  ${media.compactDesktopNavigation()} {
    margin-bottom: ${SPACING_1};
  }
  & div {
    ${media.greaterThan('navigationBreakpoint')} {
      width: var(--navigation-large-button-size);
      height: var(--navigation-large-button-size);
    }
    background: ${GREY_400};
    border-radius: 50%;
  }
`;

interface Props {
  loading: boolean;
  newsroomLogo?: string | null;
  pressroomSelector: boolean;
  pressroomName: string;
  itemData: MainMenuItem;
  siteCode?: string;
  isSubNavOpen: MenuState | null;
  open: () => void;
  close: () => void;
}

const LogoOrPressroomSelector = ({
  loading,
  newsroomLogo,
  pressroomSelector,
  pressroomName,
  siteCode,
  itemData,
  isSubNavOpen,
  open,
  close,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.navigationBreakpoint - 1}px)`);
  const size = isMobile ? imgSizes.mobile : imgSizes.desktop;
  const ref = useRef<HTMLLIElement>(null);
  const ariaLabelledby = `nav-item-button-${itemData.menuState}`;
  const ariaControls = `sub-nav-${itemData.menuState}`;

  const isOpen = isSubNavOpen === MenuState.PressroomSelector;

  const handleOnClick = () => {
    isOpen ? close() : open();
  };

  useClickOutside(ref, () => {
    isOpen && close();
  });

  if (loading) {
    return (
      <LogoSkeleton>
        <div />
      </LogoSkeleton>
    );
  } else {
    return (
      <NavItem ref={ref}>
        {pressroomSelector ? (
          <Tooltip content={t('menu_pressroom_heading')} placement="right" hidden={isOpen}>
            <ChangePressroomButton
              onClick={handleOnClick}
              aria-label={t('menu_pressroom_heading')}
              type="button"
            >
              <ImgWrapper>
                <img src={newsroomLogo || MndLogotype} alt="" width={size} height={size} />
              </ImgWrapper>
              {siteCode && isFlagCode(siteCode) && (
                <FlagWrapper $isMobile={isMobile}>
                  <Flag code={siteCode} size={isMobile ? '16px' : '20px'} />
                </FlagWrapper>
              )}
            </ChangePressroomButton>
          </Tooltip>
        ) : (
          <Tooltip content={pressroomName} placement="right" hidden={isOpen}>
            <ImgWrapper>
              <img src={newsroomLogo || MndLogotype} alt="" width={size} height={size} />
            </ImgWrapper>
          </Tooltip>
        )}
        {pressroomSelector && (
          <SubMenu
            show={isOpen}
            itemData={itemData}
            offsetTop={24}
            showSubNavOnHover={false}
            topGroup={true}
            pressroomSelector={itemData.menuState === MenuState.PressroomSelector}
            ariaId={ariaControls}
            ariaLabelledby={ariaLabelledby}
            siteCode={siteCode}
            close={close}
          />
        )}
      </NavItem>
    );
  }
};

export default LogoOrPressroomSelector;
